import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usersAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';
import Avatar from '../../components/Avatar';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState({ show: false, userId: null, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, status, appliedSearch]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll({
        status,
        search: appliedSearch,
        page: currentPage,
        limit: 20,
      });

      if (response.data.success) {
        setUsers(response.data.data.users);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showToast('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const handleSuspend = async (userId) => {
    try {
      await usersAPI.suspend(userId);
      showToast('User suspended successfully', 'success');
      fetchUsers();
    } catch (error) {
      showToast('Failed to suspend user', 'error');
    }
    setConfirmModal({ show: false, userId: null, action: null });
  };

  const handleRestore = async (userId) => {
    try {
      await usersAPI.restore(userId);
      showToast('User restored successfully', 'success');
      fetchUsers();
    } catch (error) {
      showToast('Failed to restore user', 'error');
    }
    setConfirmModal({ show: false, userId: null, action: null });
  };

  const handleDelete = async (userId) => {
    try {
      await usersAPI.deletePermanent(userId);
      showToast('User permanently deleted', 'success');
      fetchUsers();
    } catch (error) {
      showToast('Failed to delete user', 'error');
    }
    setConfirmModal({ show: false, userId: null, action: null });
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const confirmAction = (userId, action) => {
    setConfirmModal({ show: true, userId, action });
  };

  const executeConfirmedAction = () => {
    const { userId, action } = confirmModal;
    if (action === 'suspend') handleSuspend(userId);
    if (action === 'restore') handleRestore(userId);
    if (action === 'delete') handleDelete(userId);
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, userId: null, action: null })}
        onConfirm={executeConfirmedAction}
        title={
          confirmModal.action === 'suspend' ? 'Suspend User' :
            confirmModal.action === 'restore' ? 'Restore User' :
              'Delete User'
        }
        message={
          confirmModal.action === 'suspend' ? 'Are you sure you want to suspend this user?' :
            confirmModal.action === 'restore' ? 'Are you sure you want to restore this user?' :
              'Are you sure you want to permanently delete this user? This action cannot be undone.'
        }
        type={confirmModal.action === 'delete' ? 'danger' : 'warning'}
      />

      <div className="page-header mb-4">
        <h2 className="page-title">App Users</h2>
        <p className="page-subtitle">Manage and monitor user accounts</p>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Status Filter</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">Active Users</option>
              <option value="deleted">Suspended Users</option>
              <option value="all">All Users</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Search</label>
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </form>
          </div>

          <div className="col-md-2">
            <button
              className="btn btn-outline-primary w-100 text-center d-flex justify-content-center align-items-center"
              onClick={fetchUsers}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Madhab</th>
                  <th>Location</th>
                  <th>Premium</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Avatar name={user.name} size={36} />
                          <div className="ms-2">
                            <div className="fw-semibold">{user.name}</div>
                            <small className="text-muted">ID: {user.id}</small>
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className="badge badge-purple">{user.madhab || 'Not Set'}</span>
                      </td>
                      <td>
                        {user.city && user.country ? (
                          <small>{user.city}, {user.country}</small>
                        ) : (
                          <small className="text-muted">Not set</small>
                        )}
                      </td>
                      <td>
                        {user.is_premium ? (
                          <span className="badge badge-warning">
                            <i className="bi bi-star-fill me-1"></i>
                            Premium
                          </span>
                        ) : (
                          <span className="badge bg-secondary">Free</span>
                        )}
                      </td>
                      <td>
                        <small>{new Date(user.created_at).toLocaleDateString()}</small>
                      </td>
                      <td>
                        {user.deleted_at ? (
                          <span className="badge badge-danger">Suspended</span>
                        ) : (
                          <span className="badge badge-success">Active</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link
                            to={`/users/${user.id}`}
                            className="btn btn-sm btn-outline-primary"
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </Link>

                          {user.deleted_at ? (
                            <>
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => confirmAction(user.id, 'restore')}
                                title="Restore User"
                              >
                                <i className="bi bi-arrow-counterclockwise"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => confirmAction(user.id, 'delete')}
                                title="Delete Permanently"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => confirmAction(user.id, 'suspend')}
                              title="Suspend User"
                            >
                              <i className="bi bi-ban"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserList;